'use client'

import { minutesToTimePickerValue, getMinutesFrom24hTime } from '@repo/utils/time'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// Full 12h with meridiem (typed) e.g. "12:34 AM" (case-insensitive)
const TIME12_REGEX = /^(0[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/i
// Bare 12h "hh:mm" (no AM/PM) for offering AM/PM choices
const TIME_HHMM_12H_REGEX = /^(0[1-9]|1[0-2]):([0-5]\d)$/
// 24h "HH:MM" (00–23)
const TIME_HHMM_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/

const to12h = (hhmm24) => {
  if (!hhmm24) return ''
  const [H, M] = hhmm24.split(':').map((n) => parseInt(n, 10))
  const mer = H >= 12 ? 'PM' : 'AM'
  const h12 = H % 12 === 0 ? 12 : H % 12
  return `${String(h12).padStart(2, '0')}:${String(M).padStart(2, '0')} ${mer}`
}

const to24hFrom12 = (hhmm12) => {
  const m = hhmm12.trim().toUpperCase().match(TIME12_REGEX)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  const mer = m[3]
  if (mer === 'AM') {
    if (h === 12) h = 0
  } else {
    if (h !== 12) h += 12
  }
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

const to24hWithMer = (hhmm, mer) => {
  const mm = hhmm.match(TIME_HHMM_12H_REGEX)
  if (!mm) return null
  let h = parseInt(mm[1], 10)
  const m = parseInt(mm[2], 10)
  const up = mer.toUpperCase()
  if (up === 'AM') {
    if (h === 12) h = 0
  } else {
    if (h !== 12) h += 12
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const TimePicker = ({
  value = null,
  onChange = () => {},
  timeStep = 15,
  sx = {},
  size = 'small',
  disabled = false,
  required = false,
  label = 'Time',
  menuZindex = 1100,
  startAt = null,
  allowTyping = true,
  enforceStep = false
}) => {
  const [inputValue, setInputValue] = useState('')

  const baseItems = useMemo(() => {
    const opts = []
    const step = Number(timeStep) || 15
    const startAtMinutes = startAt ? getMinutesFrom24hTime(startAt) : null

    for (let m = 0; m < 24 * 60; m += step) {
      const t = minutesToTimePickerValue(m)
      const option = {
        value: t.value,
        label: allowTyping ? to12h(t.value) : t.label
      }
      if (startAt) {
        if (m > startAtMinutes) opts.push(option)
      } else {
        opts.push(option)
      }
    }
    return opts
  }, [timeStep, startAt, allowTyping])

  // Normalize current value so the chip shows 12h label when typing enabled
  const normalizedValue = useMemo(() => {
    if (!value) return value
    const v = typeof value === 'string' ? value : value.value
    if (!v) return null
    return allowTyping ? { value: v, label: to12h(v) } : value
  }, [value, allowTyping])

  const validByStep = useCallback(
    (v24) => {
      if (!enforceStep) return true
      const minutes = getMinutesFrom24hTime(v24)
      const step = Number(timeStep) || 15
      return minutes % step === 0
    },
    [enforceStep, timeStep]
  )

  const validAfterStartAt = useCallback(
    (v24) => {
      if (!startAt) return true
      const minutes = getMinutesFrom24hTime(v24)
      const startMins = getMinutesFrom24hTime(startAt)
      return minutes > startMins
    },
    [startAt]
  )

  const suggestionItems = useMemo(() => {
    if (!allowTyping) return []
    const raw = inputValue.trim().toUpperCase()

    // Case A: bare 12h (no AM/PM): offer both meridiems
    const bare12 = raw.match(TIME_HHMM_12H_REGEX)
    if (bare12) {
      const hhmm = bare12[0]
      const vAM = to24hWithMer(hhmm, 'AM')
      const vPM = to24hWithMer(hhmm, 'PM')

      const suggestions = []
      if (vAM && validAfterStartAt(vAM) && validByStep(vAM) && !baseItems.some((o) => o.value === vAM)) {
        suggestions.push({ value: vAM, label: `${hhmm} AM`, __suggestion: true })
      }
      if (vPM && validAfterStartAt(vPM) && validByStep(vPM) && !baseItems.some((o) => o.value === vPM)) {
        suggestions.push({ value: vPM, label: `${hhmm} PM`, __suggestion: true })
      }
      return suggestions
    }

    // Case B: 24h typed "HH:MM": offer the exact 12h equivalent as a single option
    const raw24 = raw.match(TIME_HHMM_24H_REGEX)
    if (raw24) {
      const v24 = raw24[0]
      if (validAfterStartAt(v24) && validByStep(v24) && !baseItems.some((o) => o.value === v24)) {
        return [{ value: v24, label: to12h(v24), __suggestion: true }]
      }
      return []
    }

    return []
  }, [inputValue, allowTyping, baseItems, validAfterStartAt, validByStep])

  const items = useMemo(() => {
    return suggestionItems.length ? [...suggestionItems, ...baseItems] : baseItems
  }, [baseItems, suggestionItems])

  // Fully-typed 12h with AM/PM is creatable via Enter
  const isValidTypedTime = (s) => {
    if (!TIME12_REGEX.test(s)) return false
    const v24 = to24hFrom12(s)
    if (!v24) return false
    if (!validByStep(v24)) return false
    if (!validAfterStartAt(v24)) return false
    return true
  }

  // Mask input: exactly 4 digits with ":" inserted after 2 digits, optional " AM"/" PM"
  const handleInputChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4) // max 4 digits total
    const letters = raw.replace(/[^a-zA-Z]/g, '').toUpperCase()

    let nextCore = ''
    if (digits.length === 0) nextCore = ''
    else if (digits.length === 1) nextCore = digits
    else if (digits.length === 2)
      nextCore = `${digits}:` // add ":" after hour
    else nextCore = `${digits.slice(0, 2)}:${digits.slice(2)}` // full hh:mm / HH:MM

    let mer = ''
    if (letters.startsWith('A')) mer = ' AM'
    else if (letters.startsWith('P')) mer = ' PM'

    const next = `${nextCore}${mer}`
    setInputValue(next)
    return next
  }

  // When pressing Enter after typing a full 12h string
  const handleCreate = (typed) => {
    const trimmed = typed.trim().toUpperCase()
    if (!isValidTypedTime(trimmed)) return
    const v24 = to24hFrom12(trimmed)
    onChange({ value: v24, label: allowTyping ? to12h(v24) : v24 })
    setInputValue('')
  }

  return (
    <div data-testid="TimePicker" style={{ width: sx?.width || '100%', maxWidth: sx?.maxWidth || '100%' }}>
      <ReactSelectMenu
        clearable={true}
        creatable={allowTyping}
        disabled={disabled}
        formatCreateLabel={(input) => `Use time: ${input.trim().toUpperCase()}`}
        inputValue={allowTyping ? inputValue : undefined}
        isValidNewOption={(input) => (allowTyping ? isValidTypedTime(input.trim().toUpperCase()) : false)}
        items={items}
        label={label}
        labelKey="label"
        menuWidth="200px"
        menuZindex={menuZindex}
        name="timePicker"
        onChange={onChange}
        onCreateOption={handleCreate}
        onInputChange={allowTyping ? handleInputChange : undefined}
        required={required}
        searchable={allowTyping}
        showIdInResults={false}
        size={size}
        sx={sx}
        value={normalizedValue}
        valueKey="value"
      />
    </div>
  )
}

TimePicker.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  sx: PropTypes.object,
  timeStep: PropTypes.number,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  label: PropTypes.string,
  menuZindex: PropTypes.number,
  startAt: PropTypes.string,
  allowTyping: PropTypes.bool,
  enforceStep: PropTypes.bool
}
